// Tiny focus-trap utility. No deps, ~40 lines.
//
// Usage:
//   import { trapFocus, releaseFocus } from '@/lib/focusTrap.js'
//   trapFocus(rootElement)   // when the panel mounts
//   releaseFocus()           // before unmount
//
// Behavior:
//   - Saves document.activeElement on trap start, restores it on release.
//   - Finds the first focusable element inside the container and focuses
//     it on trap start.
//   - On Tab/Shift+Tab, if focus would leave the container, cycles to
//     the other end. If focus is already outside, pulls it back in.
//
// The escape key and backdrop click are handled by the caller (the
// onboarding tour component), not here — this utility is purely about
// Tab/Shift+Tab focus containment.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

let savedFocus = null
let container = null
let handler = null

function getFocusable(root) {
  if (!root) return []
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  )
}

function onKeyDown(e) {
  if (e.key !== 'Tab' || !container) return
  const focusable = getFocusable(container)
  if (focusable.length === 0) {
    e.preventDefault()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (e.shiftKey) {
    if (active === first || !container.contains(active)) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (active === last || !container.contains(active)) {
      e.preventDefault()
      first.focus()
    }
  }
}

export function trapFocus(element) {
  if (!element) return
  releaseFocus()
  container = element
  savedFocus = document.activeElement
  handler = onKeyDown
  window.addEventListener('keydown', handler, true)
  // Defer the initial focus so the caller can set up state (the
  // Teleport target may not be in the DOM yet on the first frame).
  requestAnimationFrame(() => {
    if (!container) return
    const focusable = getFocusable(container)
    if (focusable.length > 0) {
      focusable[0].focus()
    } else {
      container.setAttribute('tabindex', '-1')
      container.focus()
    }
  })
}

export function releaseFocus() {
  if (handler) {
    window.removeEventListener('keydown', handler, true)
    handler = null
  }
  container = null
  if (savedFocus && typeof savedFocus.focus === 'function') {
    try { savedFocus.focus() } catch { /* element may be gone */ }
  }
  savedFocus = null
}
